import { useState } from "react";
import { useTranslation } from "react-i18next";
import { IconButton, InputAdornment, TextField } from "@mui/material";
import VisibilityIcon from "@mui/icons-material/VisibilityOutlined";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOffOutlined";
import type { SchemeContent } from "@shared/types/foyer";
import { FieldBlock, InfoCallout, SegmentedChoice } from "./StartNavLayout";

/**
 * The two Information sub-sections whose fields are laid out for Start &
 * navigation rather than borrowed from the template editor. Used by the
 * Information page under Start view.
 */

/** Wi-Fi, laid out as the design draws it (frame 1748:20043). */
export function WifiFields({
  content,
  onChange,
}: {
  content: SchemeContent;
  onChange: (next: SchemeContent) => void;
}) {
  const wifi = content.wifi ?? { ssid: "" };
  const set = (part: Partial<NonNullable<SchemeContent["wifi"]>>) =>
    onChange({ ...content, wifi: { ...wifi, ...part } });
  const [show, setShow] = useState(false);
  const { t } = useTranslation(["templates", "common"]);
  return (
    <>
      <FieldBlock label={t("infoFields.ssid")}>
        <TextField
          size="small"
          fullWidth
          placeholder={t("infoFields.ssid")}
          value={wifi.ssid}
          onChange={(e) => set({ ssid: e.target.value })}
          data-testid="wifi-ssid"
        />
      </FieldBlock>
      <FieldBlock label={t("common:labels.password")}>
        <TextField
          size="small"
          fullWidth
          placeholder={t("common:labels.password")}
          type={show ? "text" : "password"}
          value={wifi.password ?? ""}
          onChange={(e) => set({ password: e.target.value || undefined })}
          data-testid="wifi-password"
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton
                  size="small"
                  aria-label={
                    show
                      ? t("infoFields.hidePassword")
                      : t("infoFields.showPassword")
                  }
                  onClick={() => setShow((s) => !s)}
                  edge="end"
                >
                  {show ? (
                    <VisibilityOffIcon fontSize="small" />
                  ) : (
                    <VisibilityIcon fontSize="small" />
                  )}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </FieldBlock>
      <FieldBlock
        label={t("infoFields.showQr")}
        help={t("infoFields.showQrHelp")}
      >
        <SegmentedChoice
          value={wifi.showQr ? "yes" : "no"}
          options={[
            { value: "yes", label: t("common:buttons.yes") },
            { value: "no", label: t("common:buttons.no") },
          ]}
          onChange={(v) => set({ showQr: v === "yes" })}
          testId="wifi-qr"
        />
      </FieldBlock>
    </>
  );
}

/** Weather, laid out as the design draws it (frame 1756:21275). */
export function WeatherFields({
  content,
  onChange,
}: {
  content: SchemeContent;
  onChange: (next: SchemeContent) => void;
}) {
  const w = content.weather ?? {};
  const { t } = useTranslation(["templates", "common"]);
  return (
    <>
      <FieldBlock
        label={t("infoFields.temperatureUnit")}
        help={t("infoFields.temperatureUnitHelp")}
      >
        <SegmentedChoice
          value={w.units === "f" ? "f" : "c"}
          options={[
            { value: "c", label: t("infoFields.celsius") },
            { value: "f", label: t("infoFields.fahrenheit") },
          ]}
          onChange={(units) =>
            onChange({ ...content, weather: { ...w, units } })
          }
          testId="weather-units"
        />
      </FieldBlock>
      <InfoCallout title={t("infoFields.locationTitle")}>
        {t("infoFields.locationText")}
      </InfoCallout>
    </>
  );
}
